import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import HomeHero from '../components/home/HomeHero';
import LatestSets from '../components/home/LatestSets';
import PromoTile from '../components/home/PromoTile';
import CardShelf from '../components/home/CardShelf';
import { sortListingsByRarity } from '../utils/sortListings';
import { pickFeatured } from '../utils/featuredListings';
import FeaturedGrid from '../components/home/FeaturedGrid';
import FaqAccordion from '../components/home/FaqAccordion';
import HomeFooter from '../components/home/HomeFooter';
import './HomePage.css';

const API = process.env.REACT_APP_API_BASE_URL;

// One fetch powers the whole page; everything below derives from it. Every
// listing goes into its category's row (no cap), so the grade filters always
// search the full set — the whole site is only a few hundred listings.
const superOf = (l) => (l?.cert?.card?.supertype || '').toLowerCase();

const HomePage = () => {
    const navigate = useNavigate();
    const [listings, setListings] = useState([]);
    const [sets, setSets] = useState([]);
    const [liveCount, setLiveCount] = useState(0);
    const [error, setError] = useState(null);

    useEffect(() => {
        const load = async () => {
            try {
                const res = await fetch(`${API}/listings?count=500`, { credentials: 'include' });
                if (!res.ok) throw new Error(res.statusText);
                const data = await res.json();
                setListings(sortListingsByRarity(data.listings || []));
            } catch (e) {
                setError(e.message);
            }
        };
        const loadCount = async () => {
            try {
                const res = await fetch(`${API}/listings/count`, { credentials: 'include' });
                if (res.ok) {
                    const data = await res.json();
                    setLiveCount(data.Count || 0);
                }
            } catch { /* non-fatal — pill just hides */ }
        };
        const loadSets = async () => {
            try {
                const res = await fetch(`${API}/sets`);
                if (res.ok) {
                    const data = await res.json();
                    setSets(Array.isArray(data) ? data : data.sets || []);
                }
            } catch { /* non-fatal — Latest row just hides */ }
        };
        load();
        loadCount();
        loadSets();
    }, []);

    // Newest 6 sets for the "Latest" carousel, plus live-listing counts per set.
    const latest = useMemo(() => {
        const newest = [...sets]
            .filter((s) => s.release_date)
            .sort((a, b) => new Date(b.release_date) - new Date(a.release_date))
            .slice(0, 6);
        const countsByName = {};
        listings.forEach((l) => {
            const setName = l.cert?.card?.set?.name || l.cert?.set;
            if (setName) countsByName[setName] = (countsByName[setName] || 0) + 1;
        });
        return { newest, countsByName };
    }, [sets, listings]);

    const shelves = useMemo(() => {
        const pokemon = listings.filter((l) => superOf(l).startsWith('pok'));
        const trainers = listings.filter((l) => superOf(l) === 'trainer');
        const energies = listings.filter((l) => superOf(l) === 'energy');

        // Same pick the search dropdown's Suggested list uses.
        const featured = pickFeatured(listings, 8);

        return { pokemon, trainers, energies, featured };
    }, [listings]);

    return (
        <div className="gh-page">
            <HomeHero />
            <LatestSets sets={latest.newest} countsByName={latest.countsByName} />
            <PromoTile />

            {liveCount > 0 && (
                <div className="gh-live">
                    <span className="gh-live-count">{liveCount} graded cards live right now</span>
                    <button type="button" className="gh-live-cta" onClick={() => navigate('/pokemon')}>
                        Explore
                    </button>
                </div>
            )}

            {error && <p className="gh-page-error">Error loading cards: {error}</p>}

            <CardShelf title="Pokémon" seeAllTo="/pokemon" listings={shelves.pokemon} limit={Infinity} />
            <CardShelf title="Trainers" seeAllTo="/trainer" listings={shelves.trainers} limit={Infinity} />
            <CardShelf title="Energies" seeAllTo="/energy" listings={shelves.energies} limit={Infinity} />

            <FeaturedGrid listings={shelves.featured} />
            <FaqAccordion />
            <HomeFooter />
        </div>
    );
};

export default HomePage;
